"use client";

import Link from "next/link";
import { Loader2 } from "lucide-react";
import { motion, useReducedMotion, type HTMLMotionProps } from "framer-motion";
import { forwardRef, type ReactNode } from "react";
import { cn } from "@/lib/utils";

type MotionButtonProps = Omit<HTMLMotionProps<"button">, "children"> & {
  isLoading?: boolean;
  loadingText?: string;
  children: ReactNode;
};

export const MotionButton = forwardRef<HTMLButtonElement, MotionButtonProps>(
  (
    {
      className,
      children,
      isLoading = false,
      loadingText,
      disabled,
      ...props
    },
    ref
  ) => {
    const reduced = useReducedMotion();

    return (
      <motion.button
        ref={ref}
        whileHover={reduced ? undefined : { scale: 1.02 }}
        whileTap={reduced ? undefined : { scale: 0.97 }}
        transition={{ duration: 0.15, ease: [0.22, 1, 0.36, 1] }}
        className={cn(className)}
        disabled={disabled || isLoading}
        {...props}
      >
        {isLoading ? (
          <span className="inline-flex items-center justify-center gap-2">
            <Loader2 size={16} className="animate-spin" aria-hidden />
            {loadingText ?? children}
          </span>
        ) : (
          children
        )}
      </motion.button>
    );
  }
);
MotionButton.displayName = "MotionButton";

interface MotionLinkButtonProps {
  href: string;
  className?: string;
  /** Applied to the motion wrapper (e.g. full-width layout). */
  wrapperClassName?: string;
  children: ReactNode;
  onClick?: () => void;
}

export function MotionLinkButton({
  href,
  className,
  wrapperClassName,
  children,
  onClick,
}: MotionLinkButtonProps) {
  const reduced = useReducedMotion();

  return (
    <motion.div
      whileHover={reduced ? undefined : { scale: 1.02 }}
      whileTap={reduced ? undefined : { scale: 0.97 }}
      transition={{ duration: 0.15, ease: [0.22, 1, 0.36, 1] }}
      className={cn("inline-flex", wrapperClassName)}
    >
      <Link href={href} className={className} onClick={onClick}>
        {children}
      </Link>
    </motion.div>
  );
}
